import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth, useCan } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import Pointage from './Pointage';
import { Search, RefreshCw, CheckCircle2, Clock, FileText, AlertTriangle, Trash2, CircleDollarSign, Check, User2, Calendar, X, Truck, Package, Copy, Link2, RotateCcw, UserCheck, Download } from 'lucide-react';

// --- Helpers de dates (ISO YYYY-MM-DD) ---
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const j = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${j}`;
};
const DATE_PRESETS = [
    { v: 'all',      label: 'Tout' },
    { v: 'today',    label: "Aujourd'hui" },
    { v: '7d',       label: '7 jours' },
    { v: '30d',      label: '30 jours' },
    { v: 'month',    label: 'Ce mois' },
    { v: 'custom',   label: 'Personnalisé' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAY_LABELS = {
    especes: 'Espèces',
    virement: 'Virement',
    cheque_ao: 'Chèque AO',
    cheque_so: 'Chèque SO',
};

export default function EspaceDemenageurs() {
    const { user } = useAuth();
    const { canDelete: canDeleteEspace } = useCan('espace_demenageurs');
    const isAdmin = user?.role === 'admin';
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [datePreset, setDatePreset] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // --- Sous-onglets : Déménagement / Livraison / Pointage (sync URL) ---
    const [searchParams, setSearchParams] = useSearchParams();
    const validTabs = ['demenagement', 'livraison', 'pointage'];
    const initialTab = validTabs.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'demenagement';
    const [activeTab, setActiveTabState] = useState(initialTab);
    const setActiveTab = (t) => {
        setActiveTabState(t);
        const sp = new URLSearchParams(searchParams);
        if (t === 'demenagement') sp.delete('tab'); else sp.set('tab', t);
        // Quand on change d'onglet principal, on retire le sous-onglet
        if (sp.get('tab') !== 'pointage') sp.delete('sub');
        setSearchParams(sp, { replace: false });
    };
    const [livraisonLink, setLivraisonLink] = useState(null); // { token, link, created_at }
    const [livLoading, setLivLoading] = useState(false);

    // --- Livraison reports (tableau) ---
    const [livRows, setLivRows] = useState([]);
    const [livRowsLoading, setLivRowsLoading] = useState(false);
    const [livSearch, setLivSearch] = useState('');
    const [livProblemFilter, setLivProblemFilter] = useState('all'); // all|oui|non
    const [livDatePreset, setLivDatePreset] = useState('all');
    const [livDateFrom, setLivDateFrom] = useState('');
    const [livDateTo, setLivDateTo] = useState('');

    const fetchLivraisonRows = async () => {
        setLivRowsLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/livraison-reports`, { withCredentials: true });
            setLivRows(res.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur chargement rapports');
        } finally {
            setLivRowsLoading(false);
        }
    };

    const deleteLivraisonReport = async (id) => {
        if (!window.confirm('Supprimer définitivement ce rapport livraison ?')) return;
        try {
            await axios.delete(`${API_URL}/api/livraison-reports/${id}`, { withCredentials: true });
            toast.success('Rapport supprimé');
            setLivRows(prev => prev.filter(x => x.id !== id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur suppression');
        }
    };

    const applyLivPreset = (preset) => {
        setLivDatePreset(preset);
        const today = new Date();
        const iso = toISO(today);
        if (preset === 'all') { setLivDateFrom(''); setLivDateTo(''); return; }
        if (preset === 'today') { setLivDateFrom(iso); setLivDateTo(iso); return; }
        if (preset === '7d') { const d=new Date(); d.setDate(d.getDate()-6); setLivDateFrom(toISO(d)); setLivDateTo(iso); return; }
        if (preset === '30d') { const d=new Date(); d.setDate(d.getDate()-29); setLivDateFrom(toISO(d)); setLivDateTo(iso); return; }
        if (preset === 'month') { const f=new Date(today.getFullYear(), today.getMonth(), 1); setLivDateFrom(toISO(f)); setLivDateTo(iso); return; }
    };
    const clearLivDates = () => { setLivDatePreset('all'); setLivDateFrom(''); setLivDateTo(''); };

    const livFiltered = livRows.filter(r => {
        if (livSearch) {
            const s = livSearch.toLowerCase();
            if (!(r.nom || '').toLowerCase().includes(s) && !(r.livreur || '').toLowerCase().includes(s)) return false;
        }
        if (livProblemFilter !== 'all' && (r.problemes || 'non') !== livProblemFilter) return false;
        if (livDateFrom || livDateTo) {
            const dp = r.date || '';
            if (!dp) return false;
            if (livDateFrom && dp < livDateFrom) return false;
            if (livDateTo && dp > livDateTo) return false;
        }
        return true;
    });

    const livStats = {
        total: livRows.length,
        ca: livRows.reduce((s, r) => s + Number(r.montant || 0), 0),
        problemes: livRows.filter(r => r.problemes === 'oui').length,
    };

    const fetchLivraisonLink = async () => {
        setLivLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/livraison-link`, { withCredentials: true });
            setLivraisonLink(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur chargement lien');
        } finally {
            setLivLoading(false);
        }
    };

    // Lazy load du lien livraison + des rapports à la 1re ouverture de l'onglet
    useEffect(() => {
        if (activeTab === 'livraison') {
            if (!livraisonLink && !livLoading) fetchLivraisonLink();
            if (livRows.length === 0 && !livRowsLoading) fetchLivraisonRows();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const fullLivraisonUrl = livraisonLink
        ? `${window.location.origin}${livraisonLink.link}`
        : '';

    const copyLivraisonLink = async () => {
        if (!fullLivraisonUrl) return;
        try {
            await navigator.clipboard.writeText(fullLivraisonUrl);
            toast.success('Lien copié');
        } catch {
            // fallback : sélection manuelle
            const inp = document.getElementById('livraison-link-input');
            if (inp) { inp.select(); document.execCommand('copy'); toast.success('Lien copié'); }
            else toast.error('Impossible de copier');
        }
    };

    const regenerateLivraisonLink = async () => {
        if (!window.confirm('Régénérer le lien ? L\'ancien lien ne fonctionnera plus.')) return;
        try {
            const res = await axios.post(`${API_URL}/api/livraison-link/regenerate`, {}, { withCredentials: true });
            setLivraisonLink({ ...(livraisonLink || {}), ...res.data });
            toast.success('Lien régénéré');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur régénération');
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/demenageur-reports`, { withCredentials: true });
            setRows(res.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const deleteReport = async (id) => {
        if (!window.confirm('Supprimer définitivement ce rapport ?')) return;
        try {
            await axios.delete(`${API_URL}/api/demenageur-reports/${id}`, { withCredentials: true });
            toast.success('Rapport supprimé');
            setRows(prev => prev.filter(x => x.id !== id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur suppression');
        }
    };

    // --- Argent status management (partagé entre Déménagement et Livraison) ---
    const [prisParOpen, setPrisParOpen] = useState(false);
    const [prisParRow, setPrisParRow] = useState(null);
    const [prisParRemark, setPrisParRemark] = useState('');
    const [prisParTarget, setPrisParTarget] = useState('demenageur'); // 'demenageur' | 'livraison'

    const setStatut = async (id, newStatut, remarque = '', target = 'demenageur') => {
        const endpoint = target === 'livraison' ? 'livraison-reports' : 'demenageur-reports';
        try {
            const res = await axios.patch(
                `${API_URL}/api/${endpoint}/${id}/statut`,
                { argent_statut: newStatut, argent_remarque: remarque },
                { withCredentials: true },
            );
            const updater = (prev) => prev.map(r => r.id === id
                ? { ...r, argent_statut: res.data.argent_statut, argent_remarque: res.data.argent_remarque }
                : r);
            if (target === 'livraison') setLivRows(updater);
            else setRows(updater);
            toast.success('Statut mis à jour');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur mise à jour');
        }
    };

    const openPrisParDialog = (row, target = 'demenageur') => {
        setPrisParRow(row);
        setPrisParTarget(target);
        setPrisParRemark(row.argent_statut === 'pris_par' ? (row.argent_remarque || '') : '');
        setPrisParOpen(true);
    };

    const confirmPrisPar = async () => {
        if (!prisParRemark.trim()) {
            toast.error('Veuillez indiquer par qui l\'argent a été pris');
            return;
        }
        await setStatut(prisParRow.id, 'pris_par', prisParRemark.trim(), prisParTarget);
        setPrisParOpen(false);
        setPrisParRow(null);
        setPrisParRemark('');
    };

    // Calcule [from, to] selon le preset actif (hors 'custom' et 'all' qui utilisent les inputs)
    const applyPreset = (preset) => {
        setDatePreset(preset);
        const today = new Date();
        const iso = toISO(today);
        if (preset === 'all') { setDateFrom(''); setDateTo(''); return; }
        if (preset === 'today') { setDateFrom(iso); setDateTo(iso); return; }
        if (preset === '7d') {
            const d = new Date(); d.setDate(d.getDate() - 6);
            setDateFrom(toISO(d)); setDateTo(iso); return;
        }
        if (preset === '30d') {
            const d = new Date(); d.setDate(d.getDate() - 29);
            setDateFrom(toISO(d)); setDateTo(iso); return;
        }
        if (preset === 'month') {
            const first = new Date(today.getFullYear(), today.getMonth(), 1);
            setDateFrom(toISO(first)); setDateTo(iso); return;
        }
        // custom : on n'écrase pas les valeurs existantes
    };

    const clearDates = () => {
        setDatePreset('all');
        setDateFrom('');
        setDateTo('');
    };

    const filtered = rows.filter(r => {
        if (search) {
            const s = search.toLowerCase();
            if (!(r.client_nom || '').toLowerCase().includes(s)
                && !(r.chef_equipe || '').toLowerCase().includes(s)) return false;
        }
        if (statusFilter !== 'all') {
            const st = r.argent_statut || 'en_attente';
            if (statusFilter !== st) return false;
        }
        // Filtre intervalle de dates (date_prestation format ISO YYYY-MM-DD)
        if (dateFrom || dateTo) {
            const dp = r.date_prestation || '';
            if (!dp) return false; // pas de date => exclu quand un filtre date est actif
            if (dateFrom && dp < dateFrom) return false;
            if (dateTo && dp > dateTo) return false;
        }
        return true;
    });

    const stats = {
        total: rows.length,
        en_attente: rows.filter(r => (r.argent_statut || 'en_attente') === 'en_attente').length,
        recupere: rows.filter(r => r.argent_statut === 'recupere').length,
        pris_par: rows.filter(r => r.argent_statut === 'pris_par').length,
        problemes: rows.filter(r => r.problemes === 'oui').length,
    };

    // --- Export Planning Mensuel PDF ---
    const now = new Date();
    const [planningOpen, setPlanningOpen] = useState(false);
    const [planningMonth, setPlanningMonth] = useState(now.getMonth() + 1);
    const [planningYear, setPlanningYear] = useState(now.getFullYear());
    const [planningDownloading, setPlanningDownloading] = useState(false);

    const downloadPlanningMensuel = async () => {
        setPlanningDownloading(true);
        try {
            const res = await fetch(
                `${API_URL}/api/espace-demenageurs/planning-mensuel-pdf?year=${planningYear}&month=${planningMonth}`,
                { credentials: 'include' }
            );
            if (!res.ok) {
                let msg = 'Erreur lors de la generation du PDF';
                try { const j = await res.json(); if (j?.detail) msg = j.detail; } catch { /* ignore */ }
                toast.error(msg);
                return;
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Planning_${planningYear}_${String(planningMonth).padStart(2, '0')}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setPlanningOpen(false);
        } catch (e) {
            toast.error('Erreur reseau lors du telechargement du PDF');
        } finally {
            setPlanningDownloading(false);
        }
    };

    const MONTHS_FR_LABELS = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
    ];
    const yearOptions = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 1 + i);

    return (
        <Layout>
            <div className="space-y-5" data-testid="espace-demenageurs">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-xl font-bold text-[#1B365D]">Espace déménageurs</h1>
                        <p className="text-xs text-gray-500">
                            {activeTab === 'demenagement'
                                ? 'Rapports terrain remontés par les équipes.'
                                : activeTab === 'livraison'
                                ? 'Lien public partageable pour les livraisons.'
                                : 'Suivi du pointage des équipes.'}
                        </p>
                    </div>
                    {activeTab === 'demenagement' && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPlanningOpen(true)}
                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#1B365D] hover:bg-[#15294a] px-3 py-1.5 rounded-lg transition-colors shadow-sm"
                                data-testid="dem-export-planning-btn"
                                title="Générer un PDF récapitulatif de toutes les prestations d'un mois"
                            >
                                <Download className="w-4 h-4" /> Exporter le planning mensuel
                            </button>
                            <button onClick={fetchData} className="inline-flex items-center gap-1.5 text-sm text-[#1B365D] hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors" data-testid="dem-refresh">
                                <RefreshCw className="w-4 h-4" /> Rafraîchir
                            </button>
                        </div>
                    )}
                    {activeTab === 'livraison' && (
                        <button onClick={fetchLivraisonRows} className="inline-flex items-center gap-1.5 text-sm text-[#1B365D] hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors" data-testid="liv-refresh">
                            <RefreshCw className="w-4 h-4" /> Rafraîchir
                        </button>
                    )}
                </div>

                {/* Switch d'onglets */}
                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="espace-tabs">
                    <button
                        onClick={() => setActiveTab('demenagement')}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                            activeTab === 'demenagement' ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'
                        }`}
                        data-testid="tab-demenagement"
                    >
                        <Truck className="w-4 h-4" /> Déménagement
                    </button>
                    <button
                        onClick={() => setActiveTab('livraison')}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                            activeTab === 'livraison' ? 'bg-white text-[#C9A962] shadow-sm' : 'text-gray-600 hover:text-[#C9A962]'
                        }`}
                        data-testid="tab-livraison"
                    >
                        <Package className="w-4 h-4" /> Livraison
                    </button>
                    <button
                        onClick={() => setActiveTab('pointage')}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                            activeTab === 'pointage' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-600 hover:text-emerald-600'
                        }`}
                        data-testid="tab-pointage"
                    >
                        <UserCheck className="w-4 h-4" /> Pointage
                    </button>
                </div>

                {activeTab === 'demenagement' && (<>
                {/* Stats cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <StatCard label="Total liens" value={stats.total} icon={FileText} color="text-[#1B365D]" />
                    <StatCard label="En attente" value={stats.en_attente} icon={Clock} color="text-red-500" />
                    <StatCard label="Récupéré" value={stats.recupere} icon={CheckCircle2} color="text-emerald-500" />
                    <StatCard label="Pris par" value={stats.pris_par} icon={User2} color="text-blue-500" />
                    <StatCard label="Problèmes" value={stats.problemes} icon={AlertTriangle} color="text-orange-500" />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 max-w-md">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input placeholder="Rechercher client ou chef d'équipe..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="dem-search" />
                    </div>
                    <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" data-testid="dem-filter-group">
                        {[
                            { v: 'all',        label: 'Tous' },
                            { v: 'en_attente', label: 'En attente' },
                            { v: 'recupere',   label: 'Récupéré' },
                            { v: 'pris_par',   label: 'Pris par' },
                        ].map(f => (
                            <button key={f.v} onClick={() => setStatusFilter(f.v)}
                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                    statusFilter === f.v ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600'
                                }`}
                                data-testid={`dem-filter-${f.v}`}
                            >{f.label}</button>
                        ))}
                    </div>
                </div>

                {/* Filtre par intervalle de dates */}
                <div className="flex flex-wrap items-center gap-3" data-testid="dem-date-filters">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Calendar className="w-4 h-4 text-[#1B365D]" />
                        <span className="font-semibold text-[#1B365D] uppercase tracking-wide">Période</span>
                    </div>
                    <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg" data-testid="dem-date-preset-group">
                        {DATE_PRESETS.map(p => (
                            <button
                                key={p.v}
                                onClick={() => applyPreset(p.v)}
                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                    datePreset === p.v ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'
                                }`}
                                data-testid={`dem-date-preset-${p.v}`}
                            >{p.label}</button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setDatePreset('custom'); }}
                            className="w-[160px] h-9 text-xs"
                            data-testid="dem-date-from"
                        />
                        <span className="text-gray-400 text-xs">→</span>
                        <Input
                            type="date"
                            value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setDatePreset('custom'); }}
                            className="w-[160px] h-9 text-xs"
                            data-testid="dem-date-to"
                        />
                        {(dateFrom || dateTo) && (
                            <button
                                onClick={clearDates}
                                className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                title="Effacer les filtres de date"
                                data-testid="dem-date-clear"
                            >
                                <X className="w-3.5 h-3.5" /> Effacer
                            </button>
                        )}
                    </div>
                    <div className="text-xs text-gray-500 ml-auto" data-testid="dem-count">
                        <span className="font-semibold text-[#1B365D]">{filtered.length}</span> / {rows.length} rapport{rows.length > 1 ? 's' : ''}
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr className="text-xs uppercase text-gray-500">
                                    <Th>Client</Th>
                                    <Th>Commercial</Th>
                                    <Th>Date</Th>
                                    <Th>Chef d'équipe</Th>
                                    <Th center>Vol. devis</Th>
                                    <Th center>Vol. réel</Th>
                                    <Th center>Écart vol.</Th>
                                    <Th center>Heure fin</Th>
                                    <Th>Paiement</Th>
                                    <Th right>Montant</Th>
                                    {isAdmin && <Th center>Argent</Th>}
                                    <Th center>Problèmes</Th>
                                    <Th>Description</Th>
                                    <Th center>Fichier</Th>
                                    {isAdmin && <Th center>Actions</Th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr><td colSpan={isAdmin ? 15 : 14} className="text-center py-10 text-gray-400">Chargement...</td></tr>
                                ) : filtered.length === 0 ? (
                                    <tr><td colSpan={isAdmin ? 15 : 14} className="text-center py-10 text-gray-400" data-testid="dem-empty">Aucun rapport pour le moment.</td></tr>
                                ) : filtered.map(r => (
                                    <tr key={r.id} className="hover:bg-gray-50" data-testid={`dem-row-${r.id}`}>
                                        <Td><div className="font-semibold text-[#1B365D]">{r.client_nom || '—'}</div>
                                            {r.status === 'pending' && <Badge className="text-[10px] mt-1 bg-orange-100 text-orange-700 border border-orange-200">En attente</Badge>}
                                        </Td>
                                        <Td className="text-gray-700">{r.commercial || <span className="text-gray-300">—</span>}</Td>
                                        <Td className="text-gray-600">{r.date_prestation || '—'}</Td>
                                        <Td className="font-medium">{r.chef_equipe || <span className="text-gray-300">—</span>}</Td>
                                        <Td center>{r.volume_devis ? `${r.volume_devis} m³` : '—'}</Td>
                                        <Td center className="font-semibold">{r.volume_reel ? `${r.volume_reel} m³` : '—'}</Td>
                                        <Td center>
                                            {r.ecart_volume === null || r.ecart_volume === undefined
                                                ? <span className="text-gray-300">—</span>
                                                : (() => {
                                                    const e = Number(r.ecart_volume);
                                                    const sign = e > 0 ? '+' : '';
                                                    const cls = e > 0 ? 'text-red-600' : e < 0 ? 'text-emerald-600' : 'text-gray-500';
                                                    return <span className={`font-semibold ${cls}`} data-testid={`dem-ecart-${r.id}`}>{sign}{e.toFixed(1)} m³</span>;
                                                })()}
                                        </Td>
                                        <Td center>{r.heure_fin || '—'}</Td>
                                        <Td>{r.paiement_type ? <Badge className="bg-gray-100 text-gray-700 text-[10px] border border-gray-200">{PAY_LABELS[r.paiement_type] || r.paiement_type}</Badge> : '—'}</Td>
                                        <Td right className="font-semibold">{r.montant_recupere ? `${Number(r.montant_recupere).toFixed(2)} €` : '—'}</Td>
                                        {isAdmin && (
                                            <Td center>
                                            {(() => {
                                                const st = r.argent_statut || 'en_attente';
                                                const cfg = {
                                                    en_attente: { label: 'En attente', cls: 'bg-red-100 text-red-700 border border-red-200' },
                                                    recupere:   { label: 'Récupéré',   cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
                                                    pris_par:   { label: 'Pris par',   cls: 'bg-blue-100 text-blue-700 border border-blue-200' },
                                                }[st] || { label: st, cls: 'bg-gray-100 text-gray-600' };
                                                return (
                                                    <div className="flex flex-col items-center gap-1">
                                                        <Badge className={`text-[10px] ${cfg.cls}`} data-testid={`argent-statut-${r.id}`}>{cfg.label}</Badge>
                                                        {st === 'pris_par' && r.argent_remarque && (
                                                            <span className="text-[10px] text-blue-700 italic" title={r.argent_remarque}>
                                                                « {r.argent_remarque.length > 18 ? r.argent_remarque.slice(0, 18) + '…' : r.argent_remarque} »
                                                            </span>
                                                        )}
                                                        <div className="flex items-center gap-1 mt-0.5">
                                                            <button
                                                                onClick={() => setStatut(r.id, 'recupere')}
                                                                className={`p-1 rounded hover:bg-emerald-100 transition-colors ${st === 'recupere' ? 'bg-emerald-100' : ''}`}
                                                                title="Récupéré"
                                                                data-testid={`argent-btn-recupere-${r.id}`}
                                                            >
                                                                <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                            </button>
                                                            <button
                                                                onClick={() => openPrisParDialog(r)}
                                                                className={`p-1 rounded hover:bg-blue-100 transition-colors ${st === 'pris_par' ? 'bg-blue-100' : ''}`}
                                                                title="Pris par…"
                                                                data-testid={`argent-btn-prispar-${r.id}`}
                                                            >
                                                                <User2 className="w-3.5 h-3.5 text-blue-600" />
                                                            </button>
                                                            {st !== 'en_attente' && (
                                                                <button
                                                                    onClick={() => setStatut(r.id, 'en_attente')}
                                                                    className="p-1 rounded hover:bg-gray-100 transition-colors"
                                                                    title="Remettre en attente"
                                                                    data-testid={`argent-btn-reset-${r.id}`}
                                                                >
                                                                    <CircleDollarSign className="w-3.5 h-3.5 text-gray-400" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </Td>
                                        )}
                                        <Td center>
                                            {r.status !== 'completed' ? '—'
                                             : r.problemes === 'oui'
                                                ? <Badge className="bg-red-100 text-red-700 border border-red-200 text-[10px]">OUI</Badge>
                                                : <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px]">NON</Badge>}
                                        </Td>
                                        <Td><div className="max-w-[220px] truncate text-gray-600" title={r.description}>{r.description || '—'}</div></Td>
                                        <Td center>
                                            {(() => {
                                                const urls = (r.fichiers_urls && r.fichiers_urls.length > 0)
                                                    ? r.fichiers_urls
                                                    : (r.fichier_url ? [r.fichier_url] : []);
                                                if (urls.length === 0) return <span className="text-gray-300">—</span>;
                                                return (
                                                    <div className="flex items-center justify-center gap-2 flex-wrap">
                                                        {urls.map((u, i) => (
                                                            <a key={i} href={`${API_URL}${u}`} target="_blank" rel="noreferrer"
                                                                className="text-[#1B365D] hover:underline font-medium text-xs"
                                                                data-testid={`dem-file-link-${r.id}-${i}`}>
                                                                Fichier {i + 1}
                                                            </a>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </Td>
                                        {isAdmin && (
                                            <Td center>
                                                <button
                                                    onClick={() => deleteReport(r.id)}
                                                    disabled={!canDeleteEspace}
                                                    className="p-1.5 rounded-md text-red-500 hover:text-white hover:bg-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-red-500"
                                                    title={!canDeleteEspace ? 'Accès refusé : permission Suppression requise' : 'Supprimer ce rapport'}
                                                    data-testid={`dem-delete-${r.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </Td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                </>)}

                {activeTab === 'livraison' && (<>
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-stretch">
                        {/* Carte lien livraison — compacte */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3" data-testid="livraison-panel">
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <div className="h-8 w-8 rounded-lg bg-[#C9A962]/10 flex items-center justify-center">
                                        <Package className="w-4 h-4 text-[#C9A962]" />
                                    </div>
                                    <div>
                                        <h2 className="text-sm font-bold text-[#1B365D] leading-tight">Lien livraison</h2>
                                        <p className="text-[10px] text-gray-500 leading-tight">Réutilisable · à partager aux livreurs</p>
                                    </div>
                                </div>

                                {livLoading && !livraisonLink ? (
                                    <div className="text-xs text-gray-400 flex-1 text-center">Chargement...</div>
                                ) : livraisonLink ? (
                                    <div className="flex gap-2 items-stretch flex-1 min-w-[260px]">
                                        <div className="relative flex-1 min-w-0">
                                            <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                id="livraison-link-input"
                                                value={fullLivraisonUrl}
                                                readOnly
                                                onClick={(e) => e.target.select()}
                                                className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 bg-gray-50 font-mono text-xs text-[#1B365D] focus:outline-none focus:ring-2 focus:ring-[#1B365D]/30 focus:border-[#1B365D] cursor-text"
                                                data-testid="livraison-link-input"
                                            />
                                        </div>
                                        <button
                                            onClick={copyLivraisonLink}
                                            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[#1B365D] text-white text-xs font-semibold hover:bg-[#152a4a] transition-colors whitespace-nowrap"
                                            data-testid="livraison-copy-btn"
                                        >
                                            <Copy className="w-3.5 h-3.5" /> Copier
                                        </button>
                                        {isAdmin && (
                                            <button
                                                onClick={regenerateLivraisonLink}
                                                className="inline-flex items-center gap-1.5 px-2.5 h-9 rounded-lg border border-red-200 bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors whitespace-nowrap"
                                                title="Régénère le token — rend l'ancien lien invalide"
                                                data-testid="livraison-regen"
                                            >
                                                <RotateCcw className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-xs text-gray-400 flex-1 text-center">Impossible de charger. <button onClick={fetchLivraisonLink} className="text-[#1B365D] underline">Réessayer</button></div>
                                )}
                            </div>
                        </div>

                        {/* Stats livraison — compactes, à droite sur grand écran */}
                        <div className="grid grid-cols-3 gap-2 lg:w-[380px]">
                            <MiniStat label="Livraisons" value={livStats.total} icon={Package} color="text-[#C9A962]" />
                            <MiniStat label="CA" value={`${livStats.ca.toFixed(0)} €`} icon={FileText} color="text-emerald-500" />
                            <MiniStat label="Pb" value={livStats.problemes} icon={AlertTriangle} color="text-red-500" />
                        </div>
                    </div>

                    {/* Filtres livraison */}
                    <div className="flex flex-wrap gap-3 items-center">
                        <div className="relative flex-1 max-w-md">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <Input placeholder="Rechercher nom ou livreur..." value={livSearch} onChange={e => setLivSearch(e.target.value)} className="pl-9" data-testid="liv-search" />
                        </div>
                        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" data-testid="liv-problem-filter">
                            {[{v:'all',l:'Tous'},{v:'non',l:'Sans pb'},{v:'oui',l:'Avec pb'}].map(f => (
                                <button key={f.v} onClick={() => setLivProblemFilter(f.v)}
                                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${livProblemFilter === f.v ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600'}`}
                                    data-testid={`liv-problem-${f.v}`}
                                >{f.l}</button>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3" data-testid="liv-date-filters">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <Calendar className="w-4 h-4 text-[#1B365D]" />
                            <span className="font-semibold text-[#1B365D] uppercase tracking-wide">Période</span>
                        </div>
                        <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg">
                            {DATE_PRESETS.map(p => (
                                <button key={p.v} onClick={() => applyLivPreset(p.v)}
                                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${livDatePreset === p.v ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}
                                    data-testid={`liv-date-preset-${p.v}`}
                                >{p.label}</button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <Input type="date" value={livDateFrom} onChange={(e) => { setLivDateFrom(e.target.value); setLivDatePreset('custom'); }} className="w-[160px] h-9 text-xs" data-testid="liv-date-from" />
                            <span className="text-gray-400 text-xs">→</span>
                            <Input type="date" value={livDateTo} onChange={(e) => { setLivDateTo(e.target.value); setLivDatePreset('custom'); }} className="w-[160px] h-9 text-xs" data-testid="liv-date-to" />
                            {(livDateFrom || livDateTo) && (
                                <button onClick={clearLivDates} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-testid="liv-date-clear">
                                    <X className="w-3.5 h-3.5" /> Effacer
                                </button>
                            )}
                        </div>
                        <div className="text-xs text-gray-500 ml-auto" data-testid="liv-count">
                            <span className="font-semibold text-[#1B365D]">{livFiltered.length}</span> / {livRows.length} rapport{livRows.length > 1 ? 's' : ''}
                        </div>
                    </div>

                    {/* Table livraison */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr className="text-xs uppercase text-gray-500">
                                        <Th>Nom</Th>
                                        <Th>Livreur</Th>
                                        <Th>Date</Th>
                                        <Th right>Montant</Th>
                                        <Th>Paiement</Th>
                                        <Th center>Problème</Th>
                                        <Th>Description</Th>
                                        <Th center>Fichiers</Th>
                                        {isAdmin && <Th center>Argent</Th>}
                                        {isAdmin && <Th center>Actions</Th>}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {livRowsLoading ? (
                                        <tr><td colSpan={isAdmin ? 10 : 8} className="text-center py-10 text-gray-400">Chargement...</td></tr>
                                    ) : livFiltered.length === 0 ? (
                                        <tr><td colSpan={isAdmin ? 10 : 8} className="text-center py-10 text-gray-400" data-testid="liv-empty">Aucun rapport livraison pour le moment.</td></tr>
                                    ) : livFiltered.map(r => (
                                        <tr key={r.id} className="hover:bg-gray-50" data-testid={`liv-row-${r.id}`}>
                                            <Td><div className="font-semibold text-[#1B365D]">{r.nom || '—'}</div></Td>
                                            <Td className="text-gray-700">{r.livreur || '—'}</Td>
                                            <Td className="text-gray-600">{r.date || '—'}</Td>
                                            <Td right className="font-semibold">{r.montant ? `${Number(r.montant).toFixed(2)} €` : '—'}</Td>
                                            <Td>{r.paiement_type ? <Badge className="bg-gray-100 text-gray-700 text-[10px] border border-gray-200">{PAY_LABELS[r.paiement_type] || r.paiement_type}</Badge> : '—'}</Td>
                                            <Td center>
                                                {r.problemes === 'oui'
                                                    ? <Badge className="bg-red-100 text-red-700 border border-red-200 text-[10px]">OUI</Badge>
                                                    : <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px]">NON</Badge>}
                                            </Td>
                                            <Td><div className="max-w-[220px] truncate text-gray-600" title={r.description}>{r.description || '—'}</div></Td>
                                            <Td center>
                                                {(r.fichiers_urls && r.fichiers_urls.length > 0) ? (
                                                    <div className="flex items-center justify-center gap-2 flex-wrap">
                                                        {r.fichiers_urls.map((u, i) => (
                                                            <a key={i} href={`${API_URL}${u}`} target="_blank" rel="noreferrer"
                                                                className="text-[#1B365D] hover:underline font-medium text-xs"
                                                                data-testid={`liv-file-${r.id}-${i}`}>
                                                                Fichier {i + 1}
                                                            </a>
                                                        ))}
                                                    </div>
                                                ) : <span className="text-gray-300">—</span>}
                                            </Td>
                                            {isAdmin && (
                                                <Td center>
                                                    {(() => {
                                                        const st = r.argent_statut || 'en_attente';
                                                        const cfg = {
                                                            en_attente: { label: 'En attente', cls: 'bg-red-100 text-red-700 border border-red-200' },
                                                            recupere:   { label: 'Récupéré',   cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
                                                            pris_par:   { label: 'Pris par',   cls: 'bg-blue-100 text-blue-700 border border-blue-200' },
                                                        }[st] || { label: st, cls: 'bg-gray-100 text-gray-600' };
                                                        return (
                                                            <div className="flex flex-col items-center gap-1">
                                                                <Badge className={`text-[10px] ${cfg.cls}`} data-testid={`liv-argent-statut-${r.id}`}>{cfg.label}</Badge>
                                                                {st === 'pris_par' && r.argent_remarque && (
                                                                    <span className="text-[10px] text-blue-700 italic" title={r.argent_remarque}>
                                                                        « {r.argent_remarque.length > 18 ? r.argent_remarque.slice(0, 18) + '…' : r.argent_remarque} »
                                                                    </span>
                                                                )}
                                                                <div className="flex items-center gap-1 mt-0.5">
                                                                    <button
                                                                        onClick={() => setStatut(r.id, 'recupere', '', 'livraison')}
                                                                        className={`p-1 rounded hover:bg-emerald-100 transition-colors ${st === 'recupere' ? 'bg-emerald-100' : ''}`}
                                                                        title="Récupéré"
                                                                        data-testid={`liv-argent-btn-recupere-${r.id}`}
                                                                    >
                                                                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => openPrisParDialog(r, 'livraison')}
                                                                        className={`p-1 rounded hover:bg-blue-100 transition-colors ${st === 'pris_par' ? 'bg-blue-100' : ''}`}
                                                                        title="Pris par…"
                                                                        data-testid={`liv-argent-btn-prispar-${r.id}`}
                                                                    >
                                                                        <User2 className="w-3.5 h-3.5 text-blue-600" />
                                                                    </button>
                                                                    {st !== 'en_attente' && (
                                                                        <button
                                                                            onClick={() => setStatut(r.id, 'en_attente', '', 'livraison')}
                                                                            className="p-1 rounded hover:bg-gray-100 transition-colors"
                                                                            title="Remettre en attente"
                                                                            data-testid={`liv-argent-btn-reset-${r.id}`}
                                                                        >
                                                                            <CircleDollarSign className="w-3.5 h-3.5 text-gray-400" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </Td>
                                            )}
                                            {isAdmin && (
                                                <Td center>
                                                    <button
                                                        onClick={() => deleteLivraisonReport(r.id)}
                                                        disabled={!canDeleteEspace}
                                                        className="p-1.5 rounded-md text-red-500 hover:text-white hover:bg-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-red-500"
                                                        title={!canDeleteEspace ? 'Accès refusé : permission Suppression requise' : 'Supprimer ce rapport'}
                                                        data-testid={`liv-delete-${r.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </Td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>)}

                {activeTab === 'pointage' && <Pointage />}
            </div>
            {/* Dialog: "Pris par" — demande une remarque obligatoire */}
            <Dialog open={prisParOpen} onOpenChange={setPrisParOpen}>
                <DialogContent className="max-w-md" data-testid="dem-prispar-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-2">
                            <User2 className="w-4 h-4 text-blue-600" />
                            Argent pris par...
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 pt-1">
                        {prisParRow && (
                            <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm">
                                <span className="text-gray-500">{prisParTarget === 'livraison' ? 'Nom :' : 'Client :'}</span>{' '}
                                <span className="font-semibold text-[#1B365D]">
                                    {prisParTarget === 'livraison' ? (prisParRow.nom || '—') : (prisParRow.client_nom || '—')}
                                </span>
                                {(() => {
                                    const m = prisParTarget === 'livraison' ? prisParRow.montant : prisParRow.montant_recupere;
                                    return m > 0 ? (
                                        <span className="ml-2 text-gray-500">
                                            (<span className="font-semibold text-[#1B365D]">{Number(m).toFixed(2)} €</span>)
                                        </span>
                                    ) : null;
                                })()}
                            </div>
                        )}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                                Qui a pris l'argent ? <span className="text-red-500">*</span>
                            </label>
                            <Input
                                placeholder="Ex: Ahmed B., remis le 15/06 en main propre"
                                value={prisParRemark}
                                onChange={e => setPrisParRemark(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') confirmPrisPar(); }}
                                autoFocus
                                data-testid="dem-prispar-input"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">Cette remarque sera visible dans la liste.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <button
                            onClick={() => setPrisParOpen(false)}
                            className="px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors"
                            data-testid="dem-prispar-cancel"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={confirmPrisPar}
                            className="px-4 py-1.5 rounded-md text-sm font-semibold bg-[#1B365D] text-white hover:bg-[#152a4a] transition-colors"
                            data-testid="dem-prispar-confirm"
                        >
                            Enregistrer
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Export planning mensuel PDF */}
            <Dialog open={planningOpen} onOpenChange={setPlanningOpen}>
                <DialogContent className="max-w-md" data-testid="planning-mensuel-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-[#1B365D]">
                            <Download className="w-5 h-5" /> Exporter le planning mensuel
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <p className="text-sm text-gray-500">
                            Génère un PDF récapitulatif de toutes les prestations de la période, avec des cases vides pour noter les déménageurs affectés.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Mois</label>
                                <select
                                    value={planningMonth}
                                    onChange={e => setPlanningMonth(parseInt(e.target.value, 10))}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1B365D]/30"
                                    data-testid="planning-mensuel-month"
                                >
                                    {MONTHS_FR_LABELS.map((label, idx) => (
                                        <option key={idx + 1} value={idx + 1}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Année</label>
                                <select
                                    value={planningYear}
                                    onChange={e => setPlanningYear(parseInt(e.target.value, 10))}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1B365D]/30"
                                    data-testid="planning-mensuel-year"
                                >
                                    {yearOptions.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <button
                            onClick={() => setPlanningOpen(false)}
                            className="px-4 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors"
                            data-testid="planning-mensuel-cancel"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={downloadPlanningMensuel}
                            disabled={planningDownloading}
                            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-semibold bg-[#1B365D] text-white hover:bg-[#152a4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="planning-mensuel-download"
                        >
                            {planningDownloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            {planningDownloading ? 'Génération...' : 'Télécharger le PDF'}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

function StatCard({ label, value, icon: Icon, color }) {
    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gray-50 flex items-center justify-center">
                <Icon className={`w-5 h-5 ${color}`} />
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
                <div className="text-xl font-bold text-[#1B365D]">{value}</div>
            </div>
        </div>
    );
}
function MiniStat({ label, value, icon: Icon, color }) {
    return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-3 py-2 flex items-center gap-2">
            <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
            <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-wide text-gray-500 leading-tight">{label}</div>
                <div className="text-sm font-bold text-[#1B365D] leading-tight truncate">{value}</div>
            </div>
        </div>
    );
}
function Th({ children, center, right }) {
    return <th className={`px-3 py-3 text-left font-semibold ${center ? 'text-center' : ''}${right ? 'text-right' : ''}`}>{children}</th>;
}
function Td({ children, center, right, className = '' }) {
    return <td className={`px-3 py-3 align-middle ${center ? 'text-center' : ''} ${right ? 'text-right' : ''} ${className}`}>{children}</td>;
}
